// Pure math for the value charts (U-32), styled after DefiLlama's TVL chart:
// a smooth monotone line, a y-domain that hugs the data (a three-point series
// must not sit on a huge empty axis), time ticks that step from hours to days
// to months to years with the span, preset and brushed windows, and the
// header's change over a window. No DOM, so every rule is tested directly.

import { niceTicks } from "./stablecoins-chart";

/** [epoch ms, value]. */
export type ValuePoint = [number, number];

export interface Pixel {
  x: number;
  y: number;
}

export interface Window {
  from: number;
  to: number;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/**
 * A smooth line through the pixels that never overshoots the data (monotone
 * cubic, Fritsch–Carlson): a flat stretch stays flat and a peak stays a peak.
 * Two pixels give a straight segment; one gives nothing.
 */
export function monotonePath(px: readonly Pixel[]): string {
  const n = px.length;
  if (n < 2) return "";
  const f = (v: number) => v.toFixed(1);
  if (n === 2) return `M${f(px[0]!.x)},${f(px[0]!.y)} L${f(px[1]!.x)},${f(px[1]!.y)}`;
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    dx.push(px[i + 1]!.x - px[i]!.x || 1e-9);
    slope.push((px[i + 1]!.y - px[i]!.y) / dx[i]!);
  }
  const tangent: number[] = [slope[0]!];
  for (let i = 1; i < n - 1; i += 1) {
    const a = slope[i - 1]!;
    const b = slope[i]!;
    // A turn (or a flat side) gets a flat tangent; otherwise the weighted harmonic mean.
    if (a * b <= 0) tangent.push(0);
    else {
      const w1 = 2 * dx[i]! + dx[i - 1]!;
      const w2 = dx[i]! + 2 * dx[i - 1]!;
      tangent.push((w1 + w2) / (w1 / a + w2 / b));
    }
  }
  tangent.push(slope[n - 2]!);
  let d = `M${f(px[0]!.x)},${f(px[0]!.y)}`;
  for (let i = 0; i < n - 1; i += 1) {
    const h = dx[i]! / 3;
    d += ` C${f(px[i]!.x + h)},${f(px[i]!.y + tangent[i]! * h)} ${f(px[i + 1]!.x - h)},${f(px[i + 1]!.y - tangent[i + 1]! * h)} ${f(px[i + 1]!.x)},${f(px[i + 1]!.y)}`;
  }
  return d;
}

/** The line closed down to the baseline, for the gradient fill. */
export function areaPath(line: string, px: readonly Pixel[], baseline: number): string {
  if (!line || px.length < 2) return "";
  return `${line} L${px[px.length - 1]!.x.toFixed(1)},${baseline.toFixed(1)} L${px[0]!.x.toFixed(1)},${baseline.toFixed(1)} Z`;
}

/**
 * The value axis: the data's own range with a little head room, never forced
 * down to zero, and a sensible band for a flat or near-flat series, plus
 * round ticks inside it.
 */
export function valueDomain(values: readonly number[], tickCount = 4): { lo: number; hi: number; ticks: number[] } {
  if (!values.length) return { lo: 0, hi: 1, ticks: [] };
  const min = Math.min(...values);
  const max = Math.max(...values);
  // A flat or near-flat series gets a band of ±0.5% around its level, so a wobble isn't a cliff.
  const floor = Math.abs(max) * 0.01 || 1;
  const span = Math.max(max - min, floor);
  const mid = (max + min) / 2;
  const lo = Math.max(mid - span / 2 - span * 0.15, min >= 0 ? 0 : -Infinity);
  const hi = mid + span / 2 + span * 0.15;
  const ticks = niceTicks(lo, hi, tickCount).filter((v) => v >= lo && v <= hi);
  return { lo, hi, ticks };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export interface TimeTick {
  t: number;
  label: string;
}

/**
 * Date ticks for a span, at most `maxTicks`, on UTC boundaries: hours ("14:00",
 * with the date at midnight), days ("Oct 6"), months ("Oct", with the year in
 * January or at the first tick) or years ("2026"). `minStep` keeps a daily
 * series off hour ticks.
 */
export function timeTicks(t0: number, t1: number, maxTicks = 5, minStep = 0): TimeTick[] {
  if (!(t1 > t0)) return [{ t: t0, label: dayLabel(t0) }];
  const span = t1 - t0;
  const hourSteps = [1, 3, 6, 12].map((h) => h * HOUR);
  const daySteps = [1, 2, 7, 14].map((d) => d * DAY);
  for (const step of [...hourSteps, ...daySteps]) {
    if (step < minStep) continue;
    if (span / step <= maxTicks) {
      const ticks: TimeTick[] = [];
      for (let t = Math.ceil(t0 / step) * step; t <= t1; t += step) {
        const midnight = t % DAY === 0;
        ticks.push({ t, label: step < DAY && !midnight ? hourLabel(t) : dayLabel(t) });
      }
      if (ticks.length) return ticks;
    }
  }
  for (const months of [1, 3, 6, 12, 24, 60]) {
    const count = span / (months * 30.44 * DAY);
    if (count > maxTicks) continue;
    const start = new Date(t0);
    let y = start.getUTCFullYear();
    let m = start.getUTCMonth();
    if (Date.UTC(y, m, 1) < t0) m += 1;
    m = Math.ceil(m / months) * months;
    const ticks: TimeTick[] = [];
    for (;;) {
      const t = Date.UTC(y + Math.floor(m / 12), m % 12, 1);
      if (t > t1) break;
      const month = m % 12;
      const year = y + Math.floor(m / 12);
      ticks.push({ t, label: months >= 12 ? String(year) : month === 0 || ticks.length === 0 ? `${MONTHS[month]} ${year}` : MONTHS[month]! });
      m += months;
    }
    if (ticks.length) return ticks;
  }
  return [{ t: t0, label: dayLabel(t0) }];
}

const dayLabel = (t: number) => {
  const d = new Date(t);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};
const hourLabel = (t: number) => `${String(new Date(t).getUTCHours()).padStart(2, "0")}:00`;

/** "Oct 6, 2026" for a day, "Oct 6, 2026 14:30 UTC" when the time matters. */
export function tooltipDate(t: number, withTime: boolean): string {
  const d = new Date(t);
  const day = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  return withTime ? `${day} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} UTC` : day;
}

/** The whole series as a window. */
export const fullWindow = (points: readonly ValuePoint[]): Window => ({ from: points[0]?.[0] ?? 0, to: points[points.length - 1]?.[0] ?? 0 });

/** The last `days` of the series (all of it when shorter, or when `days` is null). */
export function presetWindow(points: readonly ValuePoint[], days: number | null): Window {
  const full = fullWindow(points);
  if (days === null) return full;
  return { from: Math.max(full.from, full.to - days * DAY), to: full.to };
}

/** The points inside a window. */
export function windowPoints(points: readonly ValuePoint[], win: Window): ValuePoint[] {
  return points.filter(([t]) => t >= win.from && t <= win.to);
}

/** The narrowest window a brush may make: two of the series' steps, so the line always has two points. */
export function minSpan(points: readonly ValuePoint[]): number {
  let step = Infinity;
  for (let i = 1; i < points.length; i += 1) step = Math.min(step, points[i]![0] - points[i - 1]![0]);
  return Number.isFinite(step) ? step * 2 : DAY;
}

/** A brushed window kept inside the series and no narrower than minSpan. */
export function clampWindow(win: Window, points: readonly ValuePoint[]): Window {
  const full = fullWindow(points);
  const span = Math.min(Math.max(win.to - win.from, minSpan(points)), full.to - full.from);
  let from = Math.max(full.from, Math.min(win.from, full.to - span));
  let to = from + span;
  if (to > full.to) {
    to = full.to;
    from = to - span;
  }
  return { from, to };
}

/** The window equals a preset (to the minute), so its button shows as on. */
export const sameWindow = (a: Window, b: Window) => Math.abs(a.from - b.from) < 60_000 && Math.abs(a.to - b.to) < 60_000;

/** The change from the window's first point to its last: percent for values, points for rates. */
export function windowChange(points: readonly ValuePoint[], kind: "pct" | "pt"): number | null {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || points.length < 2) return null;
  if (kind === "pt") return last[1] - first[1];
  return first[1] > 0 ? last[1] / first[1] - 1 : null;
}

/**
 * The like-for-like change over a window (U-32b): the value's move less what
 * products joining or leaving tracking added or took away, over the first
 * value. `coverage` is each point's coverage change (null when not computed);
 * the first point's own change is before the window and does not count. Null
 * when any day in the window is unknown, or the base is not positive: then no
 * change is shown, never the raw one.
 */
export function likeForLikeChange(points: readonly ValuePoint[], coverage: (t: number) => number | null | undefined): { pct: number | null; coverageUsd: number | null } {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || points.length < 2) return { pct: null, coverageUsd: null };
  let sum = 0;
  for (const [t] of points.slice(1)) {
    const c = coverage(t);
    if (c === null || c === undefined) return { pct: null, coverageUsd: null };
    sum += c;
  }
  if (!(first[1] > 0)) return { pct: null, coverageUsd: sum };
  return { pct: (last[1] - first[1] - sum) / first[1], coverageUsd: sum };
}

/** "−4.33%" / "+0.26 pt", signed with a real minus. */
export function changeText(change: number, kind: "pct" | "pt"): string {
  const sign = change > 0 ? "+" : change < 0 ? "−" : "";
  return kind === "pt" ? `${sign}${Math.abs(change * 100).toFixed(2)} pt` : `${sign}${Math.abs(change * 100).toFixed(2)}%`;
}

/** "over 30d" for a span, from its length. */
export function spanLabel(win: Window): string {
  const days = (win.to - win.from) / DAY;
  if (days < 1.5) return `over ${Math.max(1, Math.round(days * 24))}h`;
  if (days < 60) return `over ${Math.round(days)}d`;
  if (days < 540) return `over ${Math.round(days / 30.44)}mo`;
  return `over ${(days / 365.25).toFixed(1)}y`;
}
